import type { ClientMail } from '@game/shared-types';
import type { OnChangeFn, RowSelectionState } from '@game/ui';
import { create } from 'zustand';

import type { MailState } from './mailState';
import { defaultMail, mailDefault } from './mailState';

interface MailStore extends MailState {
  replyMail: (replyMail: ClientMail) => void;
  resetMail: () => void;
  markMail: (mailIds: string[], read: boolean) => void;
  openInboxMail: (mailId: string) => void;
  openOutboxMail: (mailId: string) => void;
  newMail: (toName: string) => void;
  addInboxMail: (mail: ClientMail) => void;
  addOutboxMail: (mail: ClientMail) => void;
  removeInboxMail: (mailId: string) => void;
  setSelectedMailRowKeys: OnChangeFn<RowSelectionState>;
}

export const store = create<MailStore>((set, get) => ({
  ...mailDefault,
  setSelectedMailRowKeys: (updater): RowSelectionState => {
    if (typeof updater === 'function') {
      const value = updater(get().selectedMailRowKeys);
      set((state) => ({ ...state, selectedMailRowKeys: value }));
      return value;
    }
    return updater;
  },
  replyMail: (replyMail: ClientMail): void => {
    set((state) => ({
      ...state,
      mailState: 'reply-mail',
      activeMail: replyMail,
    }));
  },
  resetMail: (): void => {
    set((state) => ({
      ...state,
      mailState: 'inbox',
      activeMail: null,
    }));
  },
  markMail: (mailIds: string[], read: boolean): void => {
    set((state) => {
      const mailRecords = get().inbox.map((record: ClientMail) => {
        if (mailIds.includes(record.id)) {
          const modifiedMail = record;
          modifiedMail.read = read;
          return modifiedMail;
        }
        return record;
      });
      return {
        ...state,
        inbox: mailRecords,
      };
    });
  },
  openInboxMail: (mailId: string): void => {
    const mail = get().inbox.find((item) => item.id === mailId);
    if (!mail) {
      throw new Error(`Mail with ID ${mailId} not found in inbox`);
    }

    set((state) => ({
      ...state,
      activeMail: mail,
      mailState: 'read-inbox-mail',
    }));
  },
  openOutboxMail: (mailId: string): void => {
    const mail = get().outbox.find((item) => item.id === mailId);
    if (!mail) {
      throw new Error(`Mail with ID ${mailId} not found in outbox`);
    }

    set((state) => ({
      ...state,
      activeMail: mail,
      mailState: 'read-outbox-mail',
    }));
  },
  newMail: (toName: string): void => {
    set((state) => ({
      ...state,
      mailState: 'new-mail',
      activeMail: {
        ...defaultMail,
        to: {
          name: toName,
          roles: [],
          tags: [],
        },
      },
    }));
  },
  addInboxMail: (inboxMail: ClientMail): void => {
    set((state) => ({
      ...state,
      inbox: [...state.inbox, inboxMail],
    }));
  },
  addOutboxMail: (outboxMail: ClientMail): void => {
    set((state) => ({
      ...state,
      outbox: [...state.outbox, outboxMail],
    }));
  },
  removeInboxMail: (mailId: ClientMail['id']): void => {
    set((state) => ({
      ...state,
      inbox: state.inbox.filter((item) => item.id !== mailId),
    }));
  },
}));
